import { ref } from 'vue'
import { router } from '@inertiajs/vue3'

const RELOAD_PARTIAL_KEYS = [
    'grocery_items',
    'low_stock_items',
    'stats',
    'recent_commands',
    'categories',
] as const

function reloadInventoryPageProps(): Promise<void> {
    return new Promise((resolve) => {
        router.reload({
            only: [...RELOAD_PARTIAL_KEYS],
            onFinish: () => resolve(),
            onError: (errors) => {
                console.error('Inertia reload failed after voice command:', errors)
                resolve()
            },
        })
    })
}

interface VoiceCommand {
    id: number
    transcribed_text: string
    command_type: string
    status: string
    confidence_score: number
    response_text: string
    created_at: string
    formatted_time: string
}

interface ApiResponse {
    success: boolean
    message?: string
    data?: any
    error?: string
}

export const useInventoryCommands = () => {
    const isProcessing = ref(false)
    const voiceResponse = ref('')
    const recentCommands = ref<VoiceCommand[]>([])
    const error = ref('')

    const processTextCommand = async (text: string) => {
        isProcessing.value = true
        error.value = ''

        try {
            const response = await fetch('/api/voice_commands', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                },
                body: JSON.stringify({
                    voice_command: {
                        transcribed_text: text,
                        confidence_score: 1.0
                    }
                })
            })

            const result: ApiResponse = await response.json()

            if (result.success) {
                if (!result.data) {
                    error.value = 'Unexpected response from server'
                    return {
                        response: 'Sorry, something went wrong. Please try again.',
                        shouldReadAloud: true,
                    }
                }
                voiceResponse.value = result.data.response

                // Add to recent commands
                if (result.data.command) {
                    recentCommands.value.unshift(result.data.command)
                    if (recentCommands.value.length > 10) {
                        recentCommands.value.pop()
                    }
                }

                // Refresh the page data to reflect changes
                await reloadInventoryPageProps()

                return {
                    response: result.data.response,
                    shouldReadAloud: result.data.should_read_aloud,
                }
            }

            error.value = result.error || 'Failed to process command'
            return {
                response: result.error || 'Failed to process command',
                shouldReadAloud: true,
            }
        } catch (err) {
            error.value = 'Network error occurred'
            return {
                response: 'Sorry, there was a network error. Please try again.',
                shouldReadAloud: true
            }
        } finally {
            isProcessing.value = false
        }
    }

    const reprocessCommand = async (command: VoiceCommand) => {
        isProcessing.value = true
        error.value = ''

        try {
            const response = await fetch(`/api/voice_commands/${command.id}/reprocess`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                }
            })

            const result: ApiResponse = await response.json()

            if (result.success) {
                if (!result.data) {
                    error.value = 'Unexpected response from server'
                    return {
                        response: 'Sorry, something went wrong. Please try again.',
                        shouldReadAloud: true,
                    }
                }
                voiceResponse.value = result.data.response

                // Update the command in recent commands
                const index = recentCommands.value.findIndex(c => c.id === command.id)
                if (index !== -1 && result.data.command) {
                    recentCommands.value[index] = result.data.command
                }

                // Refresh the page data to reflect changes
                await reloadInventoryPageProps()

                return {
                    response: result.data.response,
                    shouldReadAloud: result.data.should_read_aloud,
                }
            }

            error.value = result.error || 'Failed to reprocess command'
            return {
                response: result.error || 'Failed to reprocess command',
                shouldReadAloud: true,
            }
        } catch (err) {
            error.value = 'Network error occurred'
            return {
                response: 'Sorry, there was a network error. Please try again.',
                shouldReadAloud: true
            }
        } finally {
            isProcessing.value = false
        }
    }

    const clearResponse = () => {
        voiceResponse.value = ''
        error.value = ''
    }

    return {
        isProcessing,
        voiceResponse,
        recentCommands,
        error,
        processTextCommand,
        reprocessCommand,
        clearResponse
    }
} 